/**
 * Shopinfo — Shipping Calculator + CEP persistido
 * Arquivo: assets/shopinfo-shipping-calculator.js
 *
 * Persistência NATIVA do CEP via Cart API:
 *   POST /cart/update.js { attributes: { cep_entrega: "00000-000" } }
 * Legível em Liquid como {{ cart.attributes.cep_entrega }} (exposto em
 * data-saved-cep no overlay). Cache em localStorage para prefill.
 *
 * O modal tem DOIS MODOS, decididos no clique (handleOpenClick):
 *   'save'  — só persiste o CEP. Nenhuma requisição de frete. É o padrão, e o
 *             único caminho a partir do cep-box do header em qualquer página.
 *   'quote' — persiste E cota. Exige CTA de cotação E provedor que declare
 *             poder cotar agora (canQuote). Conjunção, nunca uma das duas.
 *
 * Cotação pelo par assíncrono da Cart API — é ele que traz a data do carrier:
 *   POST /cart/prepare_shipping_rates.json + polling GET /cart/async_shipping_rates.json
 * Estourado o orçamento, cai no GET /cart/shipping_rates.json síncrono.
 *
 * Os formatadores de promessa de entrega são funções PURAS em
 * window.Shopinfo.shippingFormat — contrato reusado pela TASK-052.
 *
 * Este arquivo é GLOBAL: carrega em TODA página e não conhece a PDP. Quem quiser
 * trocar a fonte de cotação registra um provedor em window.Shopinfo.calculator
 * (registerQuoteProvider) — a dependência aponta só nesse sentido, daqui não sai
 * nenhuma referência ao registrante. Sem provedor (home, coleção, e também na PDP
 * sem token), o caminho cart-based acima é o único, e nada disso quebra.
 */

const SHOPINFO_CEP_ATTR = 'cep_entrega';
const SHOPINFO_CEP_LS = 'shopinfo_cep';

// Orçamento de espera do par assíncrono: 5 sondagens x 2s = 10s.
const SHOPINFO_POLL_MAX = 5;
const SHOPINFO_POLL_MS = 2000;

// Auto-close do estado de sucesso. 2500ms, e não 1500: o container é
// role="status" (anúncio *polite*) e mover o foco antes disso trunca a fala em
// parte das combinações leitor+navegador. Cancelável por qualquer interação.
const SHOPINFO_SAVED_MS = 2500;

// Duração do slide do bottom-sheet (assets/shopinfo-shipping-calculator.css).
// O foco entra só depois dele: focar um diálogo ainda a caminho da tela move a
// viewport no meio da animação.
const SHOPINFO_OPEN_MS = 350;

// Teto de tempo do POST /cart/update.js em saveCep(). Sem ele, uma conexão
// PENDURADA (travada, não caída) nunca assenta a promise, o `finally` de
// submit() não roda e `this.saving` fica preso — formulário mudo. 8s: quase 7x
// o pior caso já medido em rede lenta (~1,2s), logo não aborta requisição
// lenta que ainda ia completar, e curto o bastante para o cliente não ficar
// sem confirmação. Estourar o teto é falha SILENCIOSA, como qualquer outra
// falha deste POST — ver o catch de saveCep().
const SHOPINFO_SAVE_TIMEOUT_MS = 8000;

// Focáveis do diálogo, para a armadilha de foco. Consultado a cada Tab porque o
// conteúdo do modal troca de estado e o conjunto muda junto.
const SHOPINFO_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
  ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Offset de despacho, aplicado só na conversão dias→data (data do carrier já o
// considera). Calibra para BAIXO na validação ao vivo, nunca para cima.
const SHOPINFO_SHIP_OFFSET = 1;

// Feriados nacionais FIXOS (MM-DD). Móveis ficam de fora: o offset D+1 absorve.
const SHOPINFO_HOLIDAYS = '01-01,04-21,05-01,09-07,10-12,11-02,11-15,11-20,12-25'.split(',');

// Correios: início da faixa (5 primeiros dígitos do CEP) → UF. Contígua 00000-99999, sem órfão.
const SHOPINFO_CEP_UF = [
  [0,'SP'],[20000,'RJ'],[29000,'ES'],[30000,'MG'],[40000,'BA'],[49000,'SE'],[50000,'PE'],
  [57000,'AL'],[58000,'PB'],[59000,'RN'],[60000,'CE'],[64000,'PI'],[65000,'MA'],[66000,'PA'],
  [68900,'AP'],[69000,'AM'],[69300,'RR'],[69400,'AM'],[69900,'AC'],[70000,'DF'],[72800,'GO'],
  [73000,'DF'],[73700,'GO'],[76800,'RO'],[77000,'TO'],[78000,'MT'],[78900,'RO'],[79000,'MS'],
  [80000,'PR'],[88000,'SC'],[90000,'RS'],
];

// Intl é caro: uma instância por formato no módulo, nunca uma por chamada.
const SHOPINFO_BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const SHOPINFO_DT_SHORT = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });
// Sem dia da semana: só a ponta inicial da faixa usa este (ver labelBare).
const SHOPINFO_DT_BARE = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short' });
// "21/10": a linha de frete da PDP (TASK-175) só tem lugar para dia/mês.
const SHOPINFO_DT_DM = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

window.Shopinfo = window.Shopinfo || {};
// Funções PURAS: não conhecem rate, fetch, DOM nem carrinho. `copy` carrega os
// templates vindos de locale ({ until, between }); %1/%2 são os slots.
// A superfície fala DATA, nunca contagem de dias (decisão do dono, 2026-08-21):
// o cliente decide por "chegará sex, 29 de ago", não por "5 dias úteis". A
// contagem em dias úteis continua sendo o cálculo, só não é mais o que se lê.
window.Shopinfo.shippingFormat = {
  // 'YYYY-MM-DD' → Date LOCAL. new Date(iso) seria UTC e voltaria um dia no BRT.
  parseDate(v) {
    const p = String(v || '').slice(0, 10).split('-');
    if (p.length !== 3) return null;
    const d = new Date(+p[0], +p[1] - 1, +p[2]);
    return isNaN(d.getTime()) ? null : d;
  },

  isOffDay(d) {
    const w = d.getDay();
    if (w === 0 || w === 6) return true;
    const mmdd = ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    return SHOPINFO_HOLIDAYS.indexOf(mmdd) !== -1;
  },

  // Soma dias ÚTEIS a partir de `base` — a própria base nunca conta.
  addBusinessDays(base, days) {
    const d = new Date(base.getTime());
    let left = Math.max(0, days);
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      if (!this.isOffDay(d)) left--;
    }
    return d;
  },

  // A PROMESSA: data por extenso curto, com dia da semana ("sex, 29 de ago").
  // É o que o cliente lê como compromisso — dia único e ponta FINAL da faixa.
  label(d) {
    return SHOPINFO_DT_SHORT.format(d).replace(/\./g, '');
  },

  // A ÂNCORA: mesma data, sem dia da semana ("27 de ago"). Só a ponta inicial
  // da faixa usa esta forma (opção B, decisão do dono 2026-08-21): repetir o
  // dia da semana nas duas pontas não informa mais e é o que estourava a
  // linha — o dia da semana fica onde ele decide, na data prometida.
  labelBare(d) {
    return SHOPINFO_DT_BARE.format(d).replace(/\./g, '');
  },

  fill(tpl, args) {
    return String(tpl).replace(/%(\d)/g, (m, i) => args[i - 1] || '');
  },

  // Único ponto de formatação de moeda do fluxo de frete: quem exibe rate fora
  // deste arquivo (a ponte da PDP) usa este, não um Intl próprio.
  money(v) {
    return SHOPINFO_BRL.format(v);
  },

  // Datas do carrier: autoritativas — sem offset e sem rótulo de dias úteis.
  fromDates(start, end, copy) {
    if (!start || !copy?.until) return '';
    if (!end || +end === +start) return this.fill(copy.until, [this.label(start)]);
    return this.fill(copy.between, [this.labelBare(start), this.label(end)]);
  },

  // Contagem de dias → data, com o offset de despacho. `baseDate` default: hoje.
  fromDays(minDays, maxDays, copy, baseDate) {
    if (!copy?.until) return '';
    const int = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };
    let lo = int(minDays);
    let hi = int(maxDays);
    if (lo === null && hi === null) return '';
    if (lo === null) lo = hi;
    if (hi === null) hi = lo;
    const base = baseDate ? new Date(baseDate) : new Date();
    const start = this.addBusinessDays(base, SHOPINFO_SHIP_OFFSET + lo);
    if (lo === hi) return this.fill(copy.until, [this.label(start)]);
    return this.fill(copy.between, [
      this.labelBare(start),
      this.label(this.addBusinessDays(base, SHOPINFO_SHIP_OFFSET + hi)),
    ]);
  },

  // Pior caso da faixa em "DD/MM" (TASK-175), com o MESMO offset de despacho
  // de `fromDays`: a linha e o inline nunca discordam da data.
  untilDayMonth(maxDays, baseDate) {
    const hi = parseInt(maxDays, 10);
    if (isNaN(hi)) return '';
    const base = baseDate ? new Date(baseDate) : new Date();
    return SHOPINFO_DT_DM.format(this.addBusinessDays(base, SHOPINFO_SHIP_OFFSET + hi));
  },
};

class ShopinfoShippingCalculator {
  constructor() {
    this.overlay = document.getElementById('shopinfo-shipping-overlay');
    this.modal = document.getElementById('shopinfo-shipping-modal');
    this.form = document.getElementById('shopinfo-shipping-form');
    this.input = document.getElementById('shopinfo-shipping-cep-input');
    this.resultsContainer = document.getElementById('shopinfo-shipping-results');
    this.resultsTitle = document.getElementById('shopinfo-shipping-results-title');
    this.resultsNote = document.getElementById('shopinfo-shipping-results-note');
    this.errorContainer = document.getElementById('shopinfo-shipping-error');
    this.loadingContainer = document.getElementById('shopinfo-shipping-loading');
    this.savedContainer = document.getElementById('shopinfo-shipping-saved');
    this.submitBtn = document.getElementById('shopinfo-shipping-submit');
    this.retryBtn = document.getElementById('shopinfo-shipping-retry');
    this.labels = document.querySelectorAll('[data-cep-label]');

    // Modo do diálogo. 'save' é o padrão porque é o caminho comum e o
    // inofensivo: só quem prova que quer cotar entra em 'quote'.
    this.mode = 'save';
    // Quem abriu, para devolver o foco no fechamento.
    this.opener = null;
    // Timers com dono: o de auto-close do sucesso e o de foco da abertura.
    // Os dois são cancelados em open() e close() — deixar um pendente move o
    // foco para dentro de um diálogo que já não está na tela.
    this.saveTimer = null;
    this.focusTimer = null;
    // Submit em voo. O modo salvar não tem estado de carregamento, então a tela
    // fica IGUAL durante o POST — sem esta trava o cliente em rede lenta manda
    // dois submits e nascem dois auto-close (ver submit()). Reposta também em
    // open(): a trava é da SESSÃO do diálogo, não da instância.
    this.saving = false;
    // Contador de SESSÃO do diálogo, incrementado em open(). submit() guarda o
    // valor antes do `await` e o confere depois: continuação de uma sessão que
    // já foi fechada não pode mexer na tela da sessão atual (ver submit()).
    this.session = 0;
    // Só destrava o scroll quem o travou (ver close()).
    this.lockedScroll = false;

    // Registro de integração (definido no fim deste arquivo). Lido aqui uma vez;
    // o provedor pode registrar-se antes OU depois desta instância nascer.
    this.reg = window.Shopinfo.calculator;

    // Copy de erro E de promessa vem de locales/* via data-msg-* — nunca daqui.
    this.msg = this.modal?.dataset || {};
    // Sem `note`: o sufixo "(dias úteis)" saiu da superfície quando o prazo
    // passou a ser exibido como data (Correção 2). Data não pede unidade.
    this.copy = { until: this.msg.msgUntil, between: this.msg.msgBetween };

    // CEP salvo: cart.attributes (server, via data-saved-cep) tem prioridade; senão localStorage.
    this.savedCep = this.getSavedCep();

    // Sem modal não há o que ligar: nem eventos, nem instância publicada.
    if (!this.modal) return;

    // Estado fechado desde o load: o diálogo é renderizado uma vez no header e
    // vale para a loja inteira. Sem isto ele nasce focável em toda página.
    // Reforço do `visibility: hidden` do CSS, para as tecnologias que não o
    // seguem; `inert` ausente em navegador antigo degrada para o CSS sozinho.
    this.modal.setAttribute('inert', '');
    this.modal.setAttribute('aria-hidden', 'true');

    this.bindEvents();

    // Só os rótulos aqui. O prefill do INPUT mudou-se para open(): o morph de
    // seção do header reescreve o campo ~300ms depois do load e apagava o valor
    // posto no construtor (medido em /collections/all: t=0 vale "01310-100",
    // t=300ms vale "" com savedCep intacto).
    if (this.savedCep) this.updateLabels(this.savedCep);

    // Auto-cálculo no load: REMOVIDO EM DEFINITIVO (decisão do dono, 2026-08-20,
    // TASK-052). Cotação é só por clique — com ou sem CEP salvo, o load da PDP
    // não faz nenhuma requisição de frete. Reativar exige, JUNTOS: custo e
    // rate-limit por cotação da Frenet confirmados, e os três portões do parecer
    // de performance (pós-`load` + requestIdleCallback + IntersectionObserver no
    // inline com rootMargin 200px). Sem os dois, não volta.

    // Por último: só uma instância pronta vale ser publicada para o provedor.
    this.reg.instance = this;
  }

  // CEP de 8 dígitos → UF. Estático: testável no console sem instanciar a classe.
  static ufFromCep(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length !== 8) return '';
    const prefix = parseInt(digits.slice(0, 5), 10);
    let uf = '';
    for (const r of SHOPINFO_CEP_UF) { if (prefix < r[0]) break; uf = r[1]; }
    return uf;
  }

  // Texto de terceiro (nome de rate do carrier) nunca entra cru em innerHTML.
  static esc(v) {
    const d = document.createElement('div');
    d.textContent = v == null ? '' : v;
    return d.innerHTML;
  }

  // 422 (endereço recusado) = sem cobertura; o resto é falha da loja.
  static rateError(status) {
    const err = new Error(`Erro ${status}`);
    err.shopinfoKind = status === 422 ? 'coverage' : 'network';
    return err;
  }

  getSavedCep() {
    const attr = (this.overlay?.dataset.savedCep || '').replace(/\D/g, '');
    if (attr.length === 8) return attr;
    try {
      const ls = (localStorage.getItem(SHOPINFO_CEP_LS) || '').replace(/\D/g, '');
      if (ls.length === 8) return ls;
    } catch (e) {}
    return '';
  }

  maskCep(value) {
    let v = (value || '').replace(/\D/g, '').slice(0, 8);
    if (v.length > 5) v = v.slice(0, 5) + '-' + v.slice(5);
    return v;
  }

  bindEvents() {
    // Abrir modal via qualquer CTA (cep-box do header, CTA da PDP, etc.)
    document.querySelectorAll('[data-shipping-calculator-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleOpenClick(btn));
      // a11y: elementos com role="button" (ex.: cep-box do header) abrem via Enter/Espaço.
      btn.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && btn.getAttribute('role') === 'button') {
          e.preventDefault();
          this.handleOpenClick(btn);
        }
      });
    });

    this.overlay?.addEventListener('click', () => this.close());
    document.getElementById('shopinfo-shipping-modal-close')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('is-open')) this.close();
    });

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });

    // Máscara CEP
    this.input?.addEventListener('input', (e) => {
      e.target.value = this.maskCep(e.target.value);
    });

    // O auto-close do sucesso é conveniência, não decisão: qualquer sinal de que
    // o cliente ainda está no diálogo o cancela. Sem isto o modal some debaixo
    // do dedo de quem foi corrigir o CEP logo depois de salvar.
    ['focusin', 'input', 'pointerdown'].forEach((ev) => {
      this.modal?.addEventListener(ev, () => this.clearAutoClose());
    });

    // Armadilha de foco: aberto, o Tab circula entre os focáveis do diálogo.
    // Esc continua fechando (acima) — é a saída que impede a armadilha de virar
    // prisão.
    this.modal?.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const f = this.focusables();
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  // Focáveis VISÍVEIS do diálogo, na ordem do documento. `getClientRects` corta
  // os estados escondidos por `display: none` (loading, erro, sucesso e
  // resultados se revezam) e o `hidden` do botão de repetir.
  focusables() {
    if (!this.modal) return [];
    return Array.from(this.modal.querySelectorAll(SHOPINFO_FOCUSABLE)).filter(
      (el) => !el.hasAttribute('hidden') && el.getClientRects().length > 0
    );
  }

  clearAutoClose() {
    if (this.saveTimer === null) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
  }

  // Clique em qualquer CTA de frete. O provedor registrado tem direito de
  // preferência: se devolver true, ele já atendeu o clique do seu jeito (a PDP
  // usa isso para cotar direto no inline). Sem provedor, ou se ele declinar, o
  // caminho é sempre o modal.
  handleOpenClick(btn) {
    // O MODO é CONJUNÇÃO: o CTA pediu cotação E existe provedor que declara
    // poder cotar agora. Nenhuma das duas metades basta sozinha.
    //
    // O atributo sozinho não basta: `data-shipping-quote-pdp` é escrito
    // incondicionalmente em blocks/buy-buttons.liquid, e esse bloco não é
    // exclusivo da PDP — roda na PDP esgotada e está declarado nos cards da
    // home. Decidir só por ele reproduziria a cotação indevida ali.
    //
    // O provedor sozinho também não basta: numa PDP, o cep-box do HEADER
    // encontraria provedor pronto e voltaria a cotar quando o cliente só quis
    // salvar o CEP — que é exatamente a queixa que esta tarefa corrige.
    this.mode =
      btn.hasAttribute('data-shipping-quote-pdp') && this.reg.provider?.canQuote?.()
        ? 'quote'
        : 'save';
    this.opener = btn;

    // Salvar o CEP e cotar o frete são pedidos diferentes: o botão diz qual é.
    if (this.submitBtn) {
      const label =
        this.mode === 'quote'
          ? this.submitBtn.dataset.labelQuote
          : this.submitBtn.dataset.labelSave;
      if (label) this.submitBtn.textContent = label;
    }

    const p = this.reg.provider;
    if (p?.onOpen?.(btn, this)) return;
    this.open();
  }

  open() {
    // Nenhum timer da sessão anterior sobrevive à reabertura.
    this.clearAutoClose();
    clearTimeout(this.focusTimer);

    // Nem a trava de submit. Ela era reposta SÓ no `finally` de submit(), que
    // depende de a promise de saveCep() assentar; com o POST pendurado a trava
    // atravessava o fechar-e-reabrir e o cliente reabria num formulário mudo —
    // justamente o gesto de quem achou que a tela travou. Repor aqui é seguro
    // porque finishSave() já limpa o timer da sessão anterior.
    this.saving = false;

    // Nova sessão do diálogo. O que a sessão anterior deixou em voo perde o
    // direito de escrever na tela a partir daqui — ver a guarda em submit().
    this.session++;

    // A reabertura parte SEMPRE do formulário: "CEP salvo", erro ou lista de
    // resultados de uma sessão anterior não podem estar na tela quando o
    // diálogo volta.
    this.hideAll();

    this.modal?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    // Aberto, o diálogo volta à ordem de tabulação e à árvore (par do close()).
    this.modal?.removeAttribute('inert');
    this.modal?.removeAttribute('aria-hidden');

    // Trava o scroll pelo mecanismo DO TEMA: `html[scroll-lock]`
    // (assets/base.css:33), o mesmo que assets/dialog.js:204-210 põe para a
    // busca, o drawer do carrinho, o quick-add e os filtros. `overflow: hidden`
    // só no <body> não segura o scroll no mobile: a página rolava sob o modal
    // quando o teclado virtual subia, o header entrava em sticky e o cep-box
    // colapsava — a cadeia inteira do defeito.
    //
    // Trava SÓ se ninguém tiver travado antes. Quem não travou não destrava —
    // ver close(). Com o atributo do tema esse guard passa a enxergar de fato a
    // busca e o drawer, coisa que a versão em estilo inline no <body> nunca fez.
    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.lockedScroll = true;
    }

    // Prefill aqui, e não no construtor: ver a nota no construtor.
    if (this.input && this.savedCep) this.input.value = this.maskCep(this.savedCep);

    this.focusTimer = setTimeout(() => {
      this.focusTimer = null;
      this.input?.focus();
      // `maxlength="9"` com o CEP dentro deixa o campo no limite: sem a seleção
      // o cliente teria de apagar à mão antes de conseguir digitar outro.
      if (this.input?.value) this.input.select();
    }, SHOPINFO_OPEN_MS);
  }

  close() {
    this.clearAutoClose();
    // Abrir e fechar em menos de 350ms deixaria este timer pendente e o foco
    // entraria num diálogo já invisível.
    clearTimeout(this.focusTimer);
    this.focusTimer = null;

    this.modal?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');

    // Só destrava o que ESTE modal travou. Pisar incondicionalmente devolvia o
    // scroll da página com a busca ou o drawer do carrinho ainda abertos por
    // baixo — e com o auto-close esse pisão passaria a acontecer sozinho.
    if (this.lockedScroll) {
      document.documentElement.removeAttribute('scroll-lock');
      this.lockedScroll = false;
    }

    // Devolve o foco a quem abriu, nas QUATRO saídas (sucesso, Esc, X, overlay)
    // — hoje o foco vai para o `body` e o Tab seguinte recomeça do topo do
    // documento. O foco sai ANTES de o diálogo virar inert: `inert` com o foco
    // dentro o joga para o `body`, que é o mesmo defeito.
    //
    // Focável, e não só presente: `isConnected` porque o morph de seção pode ter
    // levado o botão embora, e altura real porque o CTA da PDP fica `hidden`
    // enquanto o inline ocupa o lugar dele — focar nó escondido não move o foco
    // e cairia no `body` de novo.
    //
    // `offsetHeight > 0`, e NÃO `getClientRects().length > 0`: com o header em
    // sticky o cep-box colapsa em `max-height: 0; overflow: hidden`
    // (sections/header.liquid:544), e um elemento assim AINDA gera caixa — de
    // altura zero. Passava no teste antigo e recebia foco fora da tela. Pior: o
    // cep-box mobile é filho de `.header__mobile-search-row`
    // (sections/header.liquid:531), então esse `focusin` borbulhava até
    // assets/header.js:93 e o campo de busca expandia sozinho a CADA
    // fechamento, nas quatro saídas.
    //
    // Com altura real o cep-box colapsado deixa de receber foco e o foco cai no
    // `body`, como antes desta tarefa. É o correto: devolver foco a elemento
    // fora da tela violaria o mesmo WCAG 2.4.11 que este trecho veio atender.
    // No desktop, onde o cep-box está visível, nada muda.
    const back = this.opener?.isConnected && this.opener.offsetHeight > 0 ? this.opener : null;
    this.opener = null;
    if (back) back.focus();

    this.modal?.setAttribute('inert', '');
    this.modal?.setAttribute('aria-hidden', 'true');
  }

  // Persistência NATIVA do CEP: cart.attributes (+ cache localStorage).
  // `origin` vai para cart.attributes.cep_origin (só 'pdp'|'header', nunca o
  // CEP) para medir de onde o CEP vem. O form inline da PDP
  // (shopinfo-shipping-inline.js) chama sem 2º argumento, daí o padrão 'pdp';
  // o modal global passa 'header' em submit().
  async saveCep(rawCep, origin = 'pdp') {
    const masked = this.maskCep(rawCep);
    this.savedCep = rawCep.replace(/\D/g, '');
    try {
      localStorage.setItem(SHOPINFO_CEP_LS, masked);
    } catch (e) {}
    if (this.overlay) this.overlay.dataset.savedCep = masked;
    this.updateLabels(this.savedCep);

    // AbortController + setTimeout, e NÃO `AbortSignal.timeout`: o tema ainda
    // mira browser sem Popover API (assets/popover-polyfill.js), ou seja
    // anterior ao Safari 17, e `AbortSignal.timeout` só existe do Safari 16 em
    // diante. Onde faltasse, ele lançaria TypeError DENTRO deste try, o catch
    // engoliria em silêncio e o POST nunca sairia — pior que o defeito que este
    // teto conserta. `AbortController` é o idioma do próprio Horizon (quick-add,
    // cart-note, predictive-search) e não tem essa dúvida de suporte.
    const ac = new AbortController();
    const timeout = setTimeout(() => ac.abort(), SHOPINFO_SAVE_TIMEOUT_MS);

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            [SHOPINFO_CEP_ATTR]: masked,
            cep_origin: origin === 'pdp' ? 'pdp' : 'header',
          },
        }),
        signal: ac.signal,
      });
    } catch (e) {
      // Inclui o abort do teto. Falha deste POST NÃO vira erro visível: o CEP
      // já está no localStorage, no dataset e nos rótulos (acima, antes do
      // await); só a sincronia com cart.attributes se perde, e ela se refaz no
      // próximo salvamento.
      console.warn('[ShopinfoShipping] Falha ao salvar CEP em cart.attributes', e);
    } finally {
      // O teto não sobrevive à requisição — timer órfão foi o CONFIRMADO-2.
      clearTimeout(timeout);
    }
    // TASK-179: só o emissor (shopinfo-analytics.js) ouve; CEP sai em 5 dígitos.
    document.dispatchEvent(new CustomEvent('shopinfo:cep_saved', {
      detail: { cep_source: origin === 'pdp' ? 'pdp' : 'header', cep_prefix: this.savedCep.slice(0, 5) },
    }));
  }

  // Atualiza todos os rótulos de CEP (ex.: cep-box do header) com o CEP salvo.
  updateLabels(rawCep) {
    const masked = rawCep ? this.maskCep(rawCep) : '';
    this.labels.forEach((el) => {
      if (!el.dataset.cepDefault) el.dataset.cepDefault = el.textContent;
      el.textContent = masked ? 'CEP ' + masked : el.dataset.cepDefault;
    });
  }

  // ÚNICO ponto de entrada do submit. Valida, salva e só então bifurca — salvar
  // o CEP é o que os dois modos têm em comum, e acontece antes de qualquer
  // decisão sobre cotar.
  async submit() {
    // CAUSA RAIZ do auto-close órfão: o modo salvar não exibe carregamento nem
    // desabilita o botão, então durante o `await saveCep()` — um POST
    // /cart/update.js — a tela não muda em nada. Em rede lenta o cliente aperta
    // Enter de novo, e o segundo submit criava um segundo timer de auto-close;
    // o primeiro a disparar zerava `saveTimer`, apagando a única referência ao
    // segundo, que fechava o modal fora de hora. Barrar o submit concorrente
    // mata o caso na origem, sem estado visual novo e sem texto novo.
    if (this.saving) return;

    const raw = (this.input?.value || '').replace(/\D/g, '');
    if (raw.length !== 8) {
      this.showError(this.msg.msgCepInvalid, false);
      // Fora da trava de propósito: nada foi disparado, e o cliente tem de
      // poder corrigir e submeter de novo no ato.
      return;
    }

    this.saving = true;
    // A sessão do diálogo em que ESTE submit nasceu. Com o teto de 8s do POST,
    // uma requisição pendurada sempre volta — e antes voltava chamando
    // finishSave() sobre o diálogo que o cliente já tinha fechado e reaberto:
    // o formulário sumia no meio da digitação, com o CEP antigo nos rótulos, e
    // o modal novo fechava sozinho 2,5s depois. Fora da sessão, a continuação
    // não escreve nada na tela. O `finally` continua zerando `saving` porque
    // essa trava é da sessão atual, e a atual não tem submit em voo.
    const session = this.session;
    try {
      await this.saveCep(raw, 'header');
      if (session !== this.session) return;

      if (this.mode === 'save') {
        this.finishSave();
        return;
      }
      await this.calculate(raw);
    } finally {
      // `finally` cobre TODAS as saídas — retorno normal, o `return` do ramo
      // salvar e qualquer exceção. A trava nunca fica presa.
      this.saving = false;
    }
  }

  // Sucesso do modo salvar. NENHUMA requisição de frete passa por aqui: o CEP já
  // está no localStorage, em cart.attributes e nos rótulos, e era a cotação que
  // custava ~10s e devolvia "nenhuma transportadora atende este CEP" a quem só
  // quis guardar o CEP.
  finishSave() {
    // Um timer, com dono. O auto-close pendente morre ANTES de nascer o
    // próximo: dois `finishSave()` deixavam dois timers, e o primeiro a
    // disparar zerava `saveTimer` — o segundo virava órfão e fechava o modal
    // depois, possivelmente com o cliente digitando dentro. Também encurtava o
    // "CEP salvo" para menos que os 2500ms, que existem porque abaixo disso o
    // anúncio do role="status" é truncado. A guarda `saving` de submit() cobre
    // o caso comum; esta linha mantém a invariante em qualquer caminho.
    this.clearAutoClose();
    this.hideAll();

    if (this.savedContainer) {
      const text = this.savedContainer.querySelector('.shopinfo-shipping-saved__text');
      // Exibe PRIMEIRO, escreve no tick seguinte: texto injetado num container
      // `display: none` não é anunciado por role="status".
      this.savedContainer.classList.add('is-visible');
      const say = () => {
        if (text) text.textContent = this.msg.msgSaved || '';
      };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(say);
      else setTimeout(say, 0);
    }

    // Avisa quem cota o produto visto que o CEP mudou. Fire-and-forget: o CEP já
    // está salvo, e nada que aconteça do outro lado pode virar erro NESTE modal.
    try {
      this.reg.provider?.onCepSaved?.(this.savedCep, this);
    } catch (e) {
      console.warn('[ShopinfoShipping] onCepSaved falhou', e);
    }

    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.close();
    }, SHOPINFO_SAVED_MS);
  }

  // Ramo COTAR — o corpo que era o de calculate(), sem alteração de
  // comportamento; só a validação e o saveCep subiram para submit().
  //
  // Nota de projeto: com a regra de modo de handleOpenClick este ramo fica
  // inalcançável hoje — o CTA da PDP com provedor pronto é atendido pelo inline
  // em `onOpen` e nunca chega a abrir o modal. Fica no lugar, intacto e não
  // deletado, para que a revisão possa julgá-lo com o mode split já em pé.
  async calculate(raw) {
    this.showLoading();

    try {
      // O provedor registrado cota primeiro. `null` = ele não tem como cotar
      // agora (sem token, sem alvo) e o cart-based assume; `[]` é veredito dele
      // — sem cobertura — e NÃO cai no fallback. Exibir o inline, quando é o
      // caso, é responsabilidade de quem cotou; rate de carrinho na PDP seria
      // mentira, e por isso este arquivo não pinta nada fora do modal.
      const p = this.reg.provider;
      let rates = p ? await p.quote(raw, this) : null;
      // Quem cotou define o ESCOPO da lista: do provedor vem o produto visto,
      // do fallback vem o carrinho. A nota de escopo depende disso.
      const scoped = !!rates;
      if (!rates) rates = await this.fetchRates(raw);
      if (!rates.length) {
        // Sem cobertura: o caminho é corrigir o CEP, não repetir.
        this.showError(this.msg.msgNoCoverage, false);
        return;
      }
      this.showResults(rates, scoped);
    } catch (err) {
      console.error('[ShopinfoShipping]', err);
      // Falha da loja (rede/5xx) nunca acusa o CEP do cliente.
      const coverage = err?.shopinfoKind === 'coverage';
      this.showError(coverage ? this.msg.msgNoCoverage : this.msg.msgNetwork, !coverage);
    }
  }

  // Par assíncrono primeiro (traz delivery_date do carrier). Sem cobertura é
  // veredito final; estouro de orçamento ou falha de rede cai no GET síncrono.
  async fetchRates(rawCep) {
    const qs = new URLSearchParams({
      'shipping_address[zip]': rawCep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': ShopinfoShippingCalculator.ufFromCep(rawCep),
    }).toString();
    try {
      return await this.fetchRatesAsync(qs);
    } catch (err) {
      if (err?.shopinfoKind === 'coverage') throw err;
      return this.fetchRatesSync(qs);
    }
  }

  async fetchRatesAsync(qs) {
    // POST sem body: o browser já emite Content-Length: 0 (que é o que evita o
    // 411 da plataforma); o header é proibido de setar à mão em fetch.
    const prep = await fetch(`/cart/prepare_shipping_rates.json?${qs}`, { method: 'POST' });
    if (!prep.ok) throw ShopinfoShippingCalculator.rateError(prep.status);

    for (let i = 0; i < SHOPINFO_POLL_MAX; i++) {
      await new Promise((r) => setTimeout(r, SHOPINFO_POLL_MS));
      const res = await fetch(`/cart/async_shipping_rates.json?${qs}`, { method: 'GET' });
      if (!res.ok) throw ShopinfoShippingCalculator.rateError(res.status);
      const data = await res.json().catch(() => null);
      // 202 + null enquanto calcula; 200 + shipping_rates quando pronto.
      if (data && data.shipping_rates) return data.shipping_rates;
    }
    // Sem kind: orçamento estourado não é veredito, é motivo para o fallback.
    throw new Error('Orcamento de polling estourado');
  }

  async fetchRatesSync(qs) {
    const res = await fetch(`/cart/shipping_rates.json?${qs}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw ShopinfoShippingCalculator.rateError(res.status);
    const data = await res.json();
    return data.shipping_rates || [];
  }

  // Fonte do prazo, em ordem: data absoluta do carrier > contagem de dias >
  // nada (e aí quem chama cai no rate.name).
  promiseFor(rate) {
    const F = window.Shopinfo.shippingFormat;
    const range = Array.isArray(rate.delivery_range) ? rate.delivery_range : [];
    const start = F.parseDate(rate.delivery_date || range[0]);
    if (start) return F.fromDates(start, F.parseDate(range[1]), this.copy);

    const days = Array.isArray(rate.delivery_days) ? rate.delivery_days : [];
    const min = rate.delivery_days_min != null ? rate.delivery_days_min : days[0];
    const max = rate.delivery_days_max != null ? rate.delivery_days_max : days[1];
    return F.fromDays(min, max, this.copy);
  }

  showLoading() {
    this.hideAll();
    this.loadingContainer?.classList.add('is-visible');
    if (this.submitBtn) this.submitBtn.disabled = true;
  }

  hideAll() {
    this.loadingContainer?.classList.remove('is-visible');
    this.resultsContainer?.classList.remove('is-visible');
    this.errorContainer?.classList.remove('is-visible');
    // Sucesso é o QUARTO estado do mesmo slot exclusivo: sai junto dos outros.
    this.savedContainer?.classList.remove('is-visible');
    // A acusação ao campo pertence ao erro que sai de cena.
    this.input?.removeAttribute('aria-invalid');
    // A nota de escopo volta a esconder-se: ela pertence à cotação que sai de
    // cena, não ao próximo estado.
    if (this.resultsNote) this.resultsNote.hidden = true;
    if (this.retryBtn) this.retryBtn.hidden = true;
    if (this.submitBtn) this.submitBtn.disabled = false;
  }

  // canRetry: só a falha de rede repete.
  showError(message, canRetry) {
    this.hideAll();
    if (!this.errorContainer) return;
    this.errorContainer.querySelector('.shopinfo-shipping-error__text').textContent = message;
    if (this.retryBtn) this.retryBtn.hidden = !canRetry;
    this.errorContainer.classList.add('is-visible');
    if (!this.input) return;
    // `aria-invalid` só quando o erro é SOBRE o CEP. `canRetry` já é o marcador
    // de falha da loja (rede/5xx) — acusar o campo do cliente ali seria mentira.
    // A mensagem é lida pelo aria-describedby que o input aponta no snippet.
    if (!canRetry) this.input.setAttribute('aria-invalid', 'true');
    // O foco volta ao campo: o caminho de saída de todo erro daqui é o CEP, e
    // sem isso o cliente tem de caçar de volta o campo que acabou de submeter.
    this.input.focus();
  }

  // `scoped`: os rates são do produto visto (provedor da PDP) e não do
  // carrinho — só então a nota de escopo pode aparecer.
  showResults(rates, scoped) {
    this.hideAll();
    if (!this.resultsContainer) return;
    // Barato primeiro: a lista serve para escolher, e o preço é o eixo.
    const sorted = [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    const list = this.resultsContainer.querySelector('.shopinfo-shipping-rates-list');
    if (list) list.innerHTML = '';
    sorted.forEach((rate) => list?.appendChild(this.buildRateItem(rate)));
    if (this.resultsTitle && this.msg.msgResultsTitle && this.savedCep) {
      this.resultsTitle.textContent = window.Shopinfo.shippingFormat.fill(
        this.msg.msgResultsTitle,
        [this.maskCep(this.savedCep)]
      );
    }
    if (this.resultsNote) this.resultsNote.hidden = !scoped;
    this.resultsContainer.classList.add('is-visible');
  }

  buildRateItem(rate) {
    const price = parseFloat(rate.price);
    // "Grátis" só quando a cotação corrente voltou zero — nunca por promessa.
    const isFree = price === 0;
    const priceFormatted = isFree ? this.msg.msgFree : SHOPINFO_BRL.format(price);
    const delivery = ShopinfoShippingCalculator.esc(this.promiseFor(rate));

    const item = document.createElement('div');
    item.className = 'shopinfo-shipping-rate';
    item.innerHTML = `
      <div>
        <div class="shopinfo-shipping-rate__name">${ShopinfoShippingCalculator.esc(rate.name)}</div>
        ${delivery ? `<div class="shopinfo-shipping-rate__delivery">${delivery}</div>` : ''}
      </div>
      <div class="shopinfo-shipping-rate__price${isFree ? ' shopinfo-shipping-rate__price--free' : ''}">${ShopinfoShippingCalculator.esc(priceFormatted)}</div>
    `;
    return item;
  }
}

// Ponto de integração ÚNICO. Existe desde a execução deste script (antes de
// qualquer instância), então quem chega depois — arquivo `defer` posterior — se
// registra sem se importar com ordem. O que sai daqui é só o que a ponte precisa
// para não reimplementar nada: os dois formatadores estáticos, a instância e o
// registro. O que NÃO existe aqui é o caminho inverso: este arquivo nunca chama
// nem nomeia o registrante, e por isso a página sem provedor não tem o que falhar.
window.Shopinfo.calculator = {
  instance: null,
  // provider: {
  //   onOpen?(btn, calc) -> bool
  //     Atendeu o clique por conta própria; `true` e o modal nem abre.
  //   canQuote?() -> bool
  //     "Posso cotar AGORA?" — fonte de pé, alvo no documento, token presente.
  //     É metade da conjunção que decide o modo do modal (ver handleOpenClick):
  //     sem provedor, ou com ele declinando, o modo é sempre `save`.
  //   quote(cep, calc) -> Promise<rates>|null
  //     Cotação do produto visto. `null` devolve o turno ao cart-based.
  //   onCepSaved?(cep, calc)
  //     O CEP mudou pelo modal em modo salvar; reconcilie o seu estado. É
  //     fire-and-forget: o que falhar aqui NUNCA vira erro visível no modal.
  // }
  // Todos opcionais exceto `quote`: a página sem provedor não tem o que falhar.
  provider: null,
  ufFromCep: ShopinfoShippingCalculator.ufFromCep,
  esc: ShopinfoShippingCalculator.esc,
  registerQuoteProvider(provider) {
    this.provider = provider;
  },
};

// Init
document.addEventListener('DOMContentLoaded', () => {
  new ShopinfoShippingCalculator();
});
